'use client';

import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface CategoriesProps {
  categories: string[]; // ex: ['Markets', 'Finance', 'Sustainability', ...]
  activeCategory: string; // slug: 'markets', 'finance', ...
  className?: string;
  elementId?: string;
}

function slugifyCategory(category: string) {
  return category.toLowerCase().replace(/\s+/g, '-');
}

export default function Categories({
  categories,
  activeCategory,
  className,
  elementId,
}: CategoriesProps) {
  const listRef = useRef<HTMLUListElement | null>(null);

  const [indicator, setIndicator] = useState({
    x: 0,
    width: 0,
  });

  useEffect(() => {
    if (!listRef.current) return;

    // acha o <li> da categoria ativa
    const activeItem = listRef.current.querySelector<HTMLLIElement>(
      `li[data-slug="${activeCategory}"]`
    );

    if (!activeItem) return;

    const listRect = listRef.current.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();

    // leva em conta o scroll horizontal do container
    const x = itemRect.left - listRect.left + listRef.current.scrollLeft;

    const width = itemRect.width;

    setIndicator({ x, width });
  }, [activeCategory, categories]);

  return (
    <div
      className={cn(
        'relative border-b-[.125rem] border-white/10 lg:w-full w-max',
        className
      )}
    >
      <ul
        ref={listRef}
        className="flex items-center justify-start lg:overflow-x-auto overflow-x-auto relative"
      >
        {categories?.map((categoryLabel, index) => {
          const slug = slugifyCategory(categoryLabel);
          const isActive = slug === activeCategory;

          return (
            <li
              key={index}
              data-slug={slug}
              className="relative w-[20.125rem] h-[8.125rem] flex flex-col items-center justify-center group"
            >
              {/* <Link
                href={`/${slug}`}
                prefetch
                scroll={false}
                className="w-full h-full flex items-center justify-center"
              >
                <span
                  className={cn(
                    'font-roboto transition-opacity duration-300 font-medium tracking-[-0.06rem] uppercase group-hover:opacity-80',
                    isActive ? 'text-white' : 'text-white/40'
                  )}
                >
                  {categoryLabel}
                </span>
              </Link> */}

              <CategoryItem elementId={elementId} slug={slug}>
                <span
                  className={cn(
                    'font-roboto transition-opacity duration-300 font-medium tracking-[-0.06rem] uppercase group-hover:opacity-80',
                    isActive ? 'text-white' : 'text-white/40'
                  )}
                >
                  {categoryLabel}
                </span>
              </CategoryItem>
            </li>
          );
        })}
      </ul>

      {indicator.width > 0 && (
        <motion.div
          className="pointer-events-none absolute bottom-0 h-[0.5rem] bg-secondary"
          style={{ width: indicator.width }}
          initial={false}
          animate={{ x: indicator.x }}
          transition={{
            delay: 0,
            type: 'spring',
            stiffness: 350,
            damping: 30,
            mass: 0.4,
          }}
        />
      )}
    </div>
  );
}

function CategoryItem({
  slug,
  children,
  elementId,
}: {
  slug: string;
  children: React.ReactNode;
  elementId?: string;
}) {
  // se vier um id, navega com âncora; senão, só a rota
  const href = elementId ? `/${slug}#${elementId}` : `/${slug}`;

  return (
    <Link
      href={href}
      prefetch
      className="w-full h-full flex items-center justify-center cursor-pointer"
    >
      {children}
    </Link>
  );
}
